import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { LayerGroup } from "../../../../../types";
import { Shape } from "../../RampEditor/RampCanvas/RampCanvas";
import { getDisplayName } from "./utils";

// Shape type icons using MUI icons
import AnchorIcon from "@mui/icons-material/Anchor";
import Crop169Icon from "@mui/icons-material/Crop169";
import ExploreIcon from "@mui/icons-material/Explore";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import LayersIcon from "@mui/icons-material/Layers";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import CircleIcon from "@mui/icons-material/RadioButtonUnchecked";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";

export type LayerRowProps = {
  shape: Shape;
  /** All shapes (used for display name generation) */
  shapes: Shape[];
  isSelected: boolean;
  isHighlighted: boolean;
  isFocused?: boolean;
  onSelect: (
    shapeId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void;
  /** Toggle visibility callback - no shapeId needed, we have the shape */
  onToggleVisibility: () => void;
  /** Toggle lock callback */
  onToggleLock: () => void;
  /** Delete callback */
  onDelete: () => void;
  /** Rename callback */
  onRename: (newName: string) => void;
  /** Move up callback */
  onMoveUp: () => void;
  /** Move down callback */
  onMoveDown: () => void;
  /** Add to group callback */
  onAddToGroup: (groupId: string) => void;
  /** Remove from group callback */
  onRemoveFromGroup: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  availableGroups: LayerGroup[];
};

type Props = LayerRowProps;

const ShapeIcon: React.FC<{
  type: Shape["type"];
  fontSize?: "small" | "inherit";
}> = ({ type, fontSize = "small" }) => {
  const iconProps = { fontSize };

  switch (type) {
    case "Line":
      return <HorizontalRuleIcon {...iconProps} />;
    case "Text":
      return <TextFieldsIcon {...iconProps} />;
    case "Area":
      return <Crop169Icon {...iconProps} />;
    case "Tie Down T":
      return <AnchorIcon {...iconProps} />;
    case "Double T":
      return <ViewCompactIcon {...iconProps} />;
    case "Circle":
      return <CircleIcon {...iconProps} />;
    case "Helipad":
      return <CircleIcon {...iconProps} />; // No specific helipad icon
    case "Compass Rose":
      return <ExploreIcon {...iconProps} />;
    case "Spot":
      return <LocalParkingIcon {...iconProps} />;
    case "Zone":
      return <LayersIcon {...iconProps} />;
    default:
      return <Crop169Icon {...iconProps} />;
  }
};

export const LayerRow: React.FC<Props> = ({
  shape,
  shapes,
  isSelected,
  isHighlighted,
  isFocused = false,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onDelete,
  onRename,
  onMoveUp,
  onMoveDown,
  onAddToGroup,
  onRemoveFromGroup,
  canMoveUp,
  canMoveDown,
  availableGroups,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState("");
  const [isHovered, setIsHovered] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [groupMenuAnchor, setGroupMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const displayName = getDisplayName(shape, shapes);
  const isVisible = shape.visible !== false;
  const isLocked = shape.locked;
  const isInGroup = !!shape.group_id;
  const menuOpen = Boolean(menuAnchor);
  const groupMenuOpen = Boolean(groupMenuAnchor);

  const handleDoubleClick = () => {
    setEditValue(displayName);
    setIsEditing(true);
  };

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditComplete = () => {
    if (editValue.trim() && editValue !== displayName) {
      onRename(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleEditComplete();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    onSelect(shape.id, {
      shiftKey: e.shiftKey,
      metaKey: e.metaKey,
      ctrlKey: e.ctrlKey,
    });
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setGroupMenuAnchor(null);
  };

  const handleGroupMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setGroupMenuAnchor(e.currentTarget);
  };

  const handleAddToGroup = (groupId: string) => {
    onAddToGroup(groupId);
    handleMenuClose();
  };

  const handleRemoveFromGroup = () => {
    onRemoveFromGroup();
    handleMenuClose();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        px: 1,
        py: 0.5,
        cursor: "pointer",
        borderRadius: 1,
        position: "relative",

        // Background states
        backgroundColor: isSelected
          ? theme.palette.action.selected
          : isHighlighted || isFocused
          ? theme.palette.action.hover
          : "transparent",
        "&:hover": {
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : theme.palette.action.hover,
        },

        // Visibility affects opacity
        opacity: isVisible ? 1 : 0.5,

        // Selection indicator
        borderLeft: isSelected
          ? `3px solid ${theme.palette.primary.main}`
          : "3px solid transparent",
        outline: isFocused ? `2px solid ${theme.palette.primary.main}` : "none",
        outlineOffset: -2,

        // Smooth transitions
        transition: "opacity 0.15s ease, background-color 0.15s ease",
      }}
    >
      {/* Move buttons - visible on hover, fixed width to prevent layout shift */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.15s ease",
          width: 20,
        }}
      >
        <Tooltip title="Move up" placement="left">
          <span>
            <IconButton
              size="small"
              disabled={!canMoveUp}
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
              sx={{ p: 0.25 }}
            >
              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down" placement="left">
          <span>
            <IconButton
              size="small"
              disabled={!canMoveDown}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
              sx={{ p: 0.25 }}
            >
              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Type icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          color: theme.palette.text.secondary,
        }}
      >
        <ShapeIcon type={shape.type} />
      </Box>

      {/* Layer name */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isEditing ? (
          <TextField
            inputRef={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditComplete}
            onKeyDown={handleKeyDown}
            size="small"
            variant="standard"
            fullWidth
            sx={{ "& input": { fontSize: "0.875rem", py: 0 } }}
          />
        ) : (
          <Tooltip title={displayName} placement="top" enterDelay={500}>
            <Typography
              variant="body2"
              noWrap
              onDoubleClick={handleDoubleClick}
              sx={{
                fontStyle: shape.display_name ? "normal" : "italic",
                color: shape.display_name
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              }}
            >
              {displayName}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Visibility toggle */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility();
        }}
        sx={{ p: 0.5 }}
      >
        {isVisible ? (
          <VisibilityIcon fontSize="small" />
        ) : (
          <VisibilityOffIcon fontSize="small" />
        )}
      </IconButton>

      {/* Lock toggle */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggleLock();
        }}
        sx={{ p: 0.5 }}
      >
        {isLocked ? (
          <LockIcon fontSize="small" />
        ) : (
          <LockOpenIcon fontSize="small" />
        )}
      </IconButton>

      {/* More menu */}
      <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.5 }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      {/* Delete button */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        sx={{ p: 0.5 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* Add to Group - only show if groups exist and shape is not in a group */}
        {!isInGroup && availableGroups.length > 0 && (
          <MenuItem onClick={handleGroupMenuOpen}>
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add to Group</ListItemText>
          </MenuItem>
        )}

        {/* Remove from Group - only show if shape is in a group */}
        {isInGroup && (
          <MenuItem onClick={handleRemoveFromGroup}>
            <ListItemIcon>
              <FolderOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from Group</ListItemText>
          </MenuItem>
        )}

        {/* Show message if no groups and not in group */}
        {!isInGroup && availableGroups.length === 0 && (
          <MenuItem disabled>
            <ListItemText secondary="No groups available" />
          </MenuItem>
        )}
      </Menu>

      {/* Group selection submenu */}
      <Menu
        anchorEl={groupMenuAnchor}
        open={groupMenuOpen}
        onClose={() => setGroupMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {availableGroups.map((group) => (
          <MenuItem key={group.id} onClick={() => handleAddToGroup(group.id)}>
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{group.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
